import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Cell, CellMap, LinkedYarn, PaletteColor, PatternRecord } from '../../domain/types';
import { YARNS, weightById, type Yarn } from '../../domain/catalogs';
import { clampDim } from '../../store/model';
import { library } from '../../store/useLibrary';

export type Tool = 'brush' | 'erase' | 'eyedrop';
const key = (r: number, c: number) => `${r}-${c}`;

interface Snapshot {
  cols: number;
  rows: number;
  cells: CellMap;
}

/** Live chart-editing engine for one pattern, autosaving to the library. */
export function useEditor(record: PatternRecord) {
  const cellsRef = useRef<CellMap>(structuredClone(record.cells));
  const [cols, setCols] = useState(record.cols);
  const [rows, setRows] = useState(record.rows);
  const [gauge, setGauge] = useState(record.gauge);
  const [custom, setCustom] = useState<PaletteColor[]>(record.palette ?? []);
  const [links, setLinks] = useState<Record<string, LinkedYarn>>(record.yarns ?? {});
  const [activeYarn, setActiveYarn] = useState<string>('cream');
  const [tool, setTool] = useState<Tool>('brush');
  const [tick, setTick] = useState(0);
  const redraw = useCallback(() => setTick((t) => t + 1), []);

  const history = useRef<Snapshot[]>([]);
  const future = useRef<Snapshot[]>([]);
  const [histLen, setHistLen] = useState(0);
  const [futLen, setFutLen] = useState(0);
  const stroke = useRef<{ before: Snapshot; touched: Set<string>; dirty: boolean } | null>(null);

  const palette: Yarn[] = useMemo(() => [...YARNS, ...custom], [custom]);
  const yarn = useCallback((id?: string | null) => palette.find((y) => y.id === id), [palette]);

  /* ---------------------------- persistence ----------------------------- */
  const saveTimer = useRef<number | undefined>(undefined);
  const persist = useCallback(() => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void library.update(record.id, {
        cells: cellsRef.current,
        cols,
        rows,
        gauge,
        palette: custom,
        yarns: links,
      });
    }, 500);
  }, [record.id, cols, rows, gauge, custom, links]);
  useEffect(() => persist(), [persist]);
  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  const snapshot = useCallback(
    (): Snapshot => ({ cols, rows, cells: structuredClone(cellsRef.current) }),
    [cols, rows]
  );
  const commit = useCallback(
    (before: Snapshot) => {
      history.current.push(before);
      if (history.current.length > 100) history.current.shift();
      future.current = [];
      setHistLen(history.current.length);
      setFutLen(0);
      persist();
    },
    [persist]
  );

  /* ------------------------------- editing ------------------------------- */
  const getColor = useCallback((r: number, c: number) => cellsRef.current[key(r, c)]?.color ?? null, []);
  const getCell = useCallback((r: number, c: number): Cell => ({ ...cellsRef.current[key(r, c)] }), []);

  const writeCell = useCallback((r: number, c: number, patch: Partial<Cell>) => {
    const k = key(r, c);
    const next: Cell = { ...cellsRef.current[k], ...patch };
    (Object.keys(next) as (keyof Cell)[]).forEach((p) => {
      if (next[p] == null || next[p] === '') delete next[p];
    });
    if (Object.keys(next).length === 0) delete cellsRef.current[k];
    else cellsRef.current[k] = next;
  }, []);

  const applyToolAt = useCallback(
    (r: number, c: number) => {
      if (r < 0 || c < 0 || r >= rows || c >= cols) return;
      if (tool === 'erase') writeCell(r, c, { color: null, fiber: null, stitch: null, weight: null, note: null });
      else if (tool === 'eyedrop') {
        const col = getColor(r, c);
        if (col) setActiveYarn(col);
        setTool('brush');
      } else writeCell(r, c, { color: activeYarn });
    },
    [tool, rows, cols, writeCell, getColor, activeYarn]
  );

  const beginStroke = useCallback(() => {
    stroke.current = { before: snapshot(), touched: new Set(), dirty: false };
  }, [snapshot]);
  const strokeAt = useCallback(
    (r: number, c: number) => {
      const s = stroke.current;
      if (!s) return;
      const k = key(r, c);
      if (s.touched.has(k)) return;
      s.touched.add(k);
      const wasEyedrop = tool === 'eyedrop';
      applyToolAt(r, c);
      if (!wasEyedrop) s.dirty = true;
      redraw();
    },
    [applyToolAt, tool, redraw]
  );
  const endStroke = useCallback(() => {
    const s = stroke.current;
    stroke.current = null;
    if (s && s.dirty) commit(s.before);
  }, [commit]);

  /* tap = one-cell stroke */
  const tap = useCallback(
    (r: number, c: number) => {
      beginStroke();
      strokeAt(r, c);
      endStroke();
    },
    [beginStroke, strokeAt, endStroke]
  );

  const restore = useCallback((snap: Snapshot) => {
    cellsRef.current = structuredClone(snap.cells);
    setCols(snap.cols);
    setRows(snap.rows);
    redraw();
  }, [redraw]);

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    future.current.push(snapshot());
    restore(prev);
    setHistLen(history.current.length);
    setFutLen(future.current.length);
    persist();
  }, [snapshot, restore, persist]);
  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next) return;
    history.current.push(snapshot());
    restore(next);
    setHistLen(history.current.length);
    setFutLen(future.current.length);
    persist();
  }, [snapshot, restore, persist]);

  const fill = useCallback(() => {
    const before = snapshot();
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) writeCell(r, c, { color: activeYarn });
    commit(before);
    redraw();
  }, [snapshot, rows, cols, writeCell, activeYarn, commit, redraw]);
  const clear = useCallback(() => {
    if (Object.keys(cellsRef.current).length === 0) return;
    const before = snapshot();
    cellsRef.current = {};
    commit(before);
    redraw();
  }, [snapshot, commit, redraw]);

  const resize = useCallback(
    (nextCols: number, nextRows: number) => {
      const nc = clampDim(nextCols);
      const nr = clampDim(nextRows);
      if (nc === cols && nr === rows) return;
      const before = snapshot();
      for (const k of Object.keys(cellsRef.current)) {
        const [r, c] = k.split('-').map(Number);
        if (r >= nr || c >= nc) delete cellsRef.current[k];
      }
      setCols(nc);
      setRows(nr);
      commit(before);
      redraw();
    },
    [cols, rows, snapshot, commit, redraw]
  );

  const addCustomColor = useCallback(
    (hex: string) => {
      const h = hex.toUpperCase();
      const existing = palette.find((y) => y.hex.toUpperCase() === h);
      if (existing) {
        setActiveYarn(existing.id);
        return;
      }
      const id = 'custom' + palette.length;
      const name = 'Custom ' + (custom.length + 1);
      setCustom((cs) => [...cs, { id, name, hex: h, custom: true }]);
      setActiveYarn(id);
    },
    [palette, custom.length]
  );

  /** Single-cell edit (loupe): colour / fibre / stitch / weight / note, one undo step. */
  const editCell = useCallback(
    (r: number, c: number, patch: Partial<Cell>) => {
      const before = snapshot();
      writeCell(r, c, patch);
      commit(before);
      redraw();
    },
    [snapshot, writeCell, commit, redraw]
  );

  const setLink = useCallback((yarnId: string, linked: LinkedYarn) => {
    setLinks((m) => ({ ...m, [yarnId]: linked }));
  }, []);
  const clearLink = useCallback((yarnId: string) => {
    setLinks((m) => {
      const next = { ...m };
      delete next[yarnId];
      return next;
    });
  }, []);

  /* ------------------------------ derived -------------------------------- */
  const stats = useMemo(() => {
    void tick;
    let painted = 0;
    const counts = new Map<string, number>();
    for (const cell of Object.values(cellsRef.current)) {
      if (cell.color) {
        painted++;
        counts.set(cell.color, (counts.get(cell.color) ?? 0) + 1);
      }
    }
    const w = weightById(gauge) ?? weightById('dk')!;
    const legend = [...counts.entries()]
      .map(([id, count]) => ({ id, count, yarn: yarn(id) }))
      .filter((e) => e.yarn)
      .sort((a, b) => b.count - a.count);
    return {
      painted,
      pct: Math.round((painted / (rows * cols)) * 100),
      widthIn: Math.round((cols / w.g) * 4 * 10) / 10,
      heightIn: Math.round((rows / (w.rg || w.g)) * 4 * 10) / 10,
      gaugeName: w.name,
      legend,
    };
  }, [tick, gauge, rows, cols, yarn]);

  return {
    cellsRef,
    cols,
    rows,
    gauge,
    setGauge,
    palette,
    yarn,
    activeYarn,
    setActiveYarn,
    tool,
    setTool,
    tick,
    getColor,
    getCell,
    editCell,
    beginStroke,
    strokeAt,
    endStroke,
    tap,
    undo,
    redo,
    canUndo: histLen > 0,
    canRedo: futLen > 0,
    fill,
    clear,
    resize,
    addCustomColor,
    links,
    setLink,
    clearLink,
    stats,
  };
}

export type EditorApi = ReturnType<typeof useEditor>;
